import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Heading,
  Text,
  HStack,
  VStack,
  Button,
  Select,
  SimpleGrid,
  Spinner,
  Badge,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Stat,
  StatLabel,
  StatNumber,
  Icon,
} from '@chakra-ui/react';
import { FiRefreshCw, FiTrendingUp, FiActivity, FiUsers, FiShield } from 'react-icons/fi';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';

const API_BASE = '/api';
const COLORS = ['#7c3aed', '#6d28d9', '#3182CE', '#38A169', '#D69E2E', '#E53E3E', '#319795', '#DD6B20'];

const ACTION_COLORS = {
  sso_login: '#7c3aed',
  sso_provision: '#38A169',
  create: '#3182CE',
  update: '#D69E2E',
  delete: '#E53E3E',
};

function UsageAnalytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  const getAuthHeaders = () => ({
    'Content-Type': 'application/json',
    'Authorization': `Token ${localStorage.getItem('sso_admin_token')}`,
  });

  const fetchAnalytics = useCallback(() => {
    setLoading(true);
    fetch(`${API_BASE}/analytics/?days=${days}`, { headers: getAuthHeaders() })
      .then(res => res.json())
      .then(d => {
        setData(d);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [days]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return `${d.getDate()}/${d.getMonth() + 1}`;
  };

  if (loading) {
    return <Box textAlign="center" py={20}><Spinner size="xl" color="purple.500" /></Box>;
  }

  if (!data) {
    return <Text color="gray.500" textAlign="center" py={20}>Failed to load analytics</Text>;
  }

  const totalAuditEvents = (data.audit_by_action || []).reduce((s, a) => s + a.count, 0);

  return (
    <Box>
      <HStack justify="space-between" mb={6}>
        <VStack align="start" spacing={1}>
          <Heading size="lg">Usage Analytics</Heading>
          <Text color="gray.500">SSO activity trends and usage patterns</Text>
        </VStack>
        <HStack>
          <Select value={days} onChange={(e) => setDays(Number(e.target.value))} w="140px" bg="white">
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
            <option value={365}>Last year</option>
          </Select>
          <Button leftIcon={<FiRefreshCw />} colorScheme="purple" onClick={fetchAnalytics}>
            Refresh
          </Button>
        </HStack>
      </HStack>

      {/* Summary stats */}
      <SimpleGrid columns={{ base: 1, md: 4 }} spacing={6} mb={8}>
        <Stat bg="white" p={6} borderRadius="lg" boxShadow="md">
          <HStack>
            <Box p={3} bg="purple.100" borderRadius="lg">
              <Icon as={FiShield} boxSize={6} color="purple.600" />
            </Box>
            <VStack align="start" spacing={0}>
              <StatLabel color="gray.500">SSO Logins</StatLabel>
              <StatNumber fontSize="2xl">{data.total_logins ?? 0}</StatNumber>
            </VStack>
          </HStack>
        </Stat>
        <Stat bg="white" p={6} borderRadius="lg" boxShadow="md">
          <HStack>
            <Box p={3} bg="green.100" borderRadius="lg">
              <Icon as={FiUsers} boxSize={6} color="green.600" />
            </Box>
            <VStack align="start" spacing={0}>
              <StatLabel color="gray.500">Provisioned Users</StatLabel>
              <StatNumber fontSize="2xl">{data.total_provisions ?? 0}</StatNumber>
            </VStack>
          </HStack>
        </Stat>
        <Stat bg="white" p={6} borderRadius="lg" boxShadow="md">
          <HStack>
            <Box p={3} bg="blue.100" borderRadius="lg">
              <Icon as={FiTrendingUp} boxSize={6} color="blue.600" />
            </Box>
            <VStack align="start" spacing={0}>
              <StatLabel color="gray.500">Active Tenants</StatLabel>
              <StatNumber fontSize="2xl">{data.enabled_tenants ?? 0} / {data.total_tenants ?? 0}</StatNumber>
            </VStack>
          </HStack>
        </Stat>
        <Stat bg="white" p={6} borderRadius="lg" boxShadow="md">
          <HStack>
            <Box p={3} bg="orange.100" borderRadius="lg">
              <Icon as={FiActivity} boxSize={6} color="orange.600" />
            </Box>
            <VStack align="start" spacing={0}>
              <StatLabel color="gray.500">Admin Events</StatLabel>
              <StatNumber fontSize="2xl">{totalAuditEvents}</StatNumber>
            </VStack>
          </HStack>
        </Stat>
      </SimpleGrid>

      {/* SSO Logins & Provisions over time */}
      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6} mb={6}>
        <Box bg="white" p={6} borderRadius="lg" boxShadow="md">
          <Heading size="sm" mb={4}>SSO Logins Over Time</Heading>
          {data.sso_logins_by_day?.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <AreaChart data={data.sso_logins_by_day}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tickFormatter={formatDate} fontSize={12} />
                <YAxis fontSize={12} />
                <Tooltip labelFormatter={(v) => new Date(v).toLocaleDateString()} />
                <Area type="monotone" dataKey="count" stroke="#7c3aed" fill="#7c3aed" fillOpacity={0.2} name="Logins" />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <Text color="gray.400" textAlign="center" py={10}>No login data</Text>
          )}
        </Box>

        <Box bg="white" p={6} borderRadius="lg" boxShadow="md">
          <Heading size="sm" mb={4}>Logins &amp; Provisions</Heading>
          {(data.sso_logins_by_day?.length > 0 || data.sso_provisions_by_day?.length > 0) ? (
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={mergeTimeSeries(data.sso_logins_by_day, data.sso_provisions_by_day)}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tickFormatter={formatDate} fontSize={12} />
                <YAxis fontSize={12} />
                <Tooltip labelFormatter={(v) => new Date(v).toLocaleDateString()} />
                <Legend />
                <Bar dataKey="logins" fill="#7c3aed" name="Logins" />
                <Bar dataKey="provisions" fill="#38A169" name="Provisions" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <Text color="gray.400" textAlign="center" py={10}>No data</Text>
          )}
        </Box>
      </SimpleGrid>

      {/* Action breakdown + Tenant activity */}
      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6} mb={6}>
        <Box bg="white" p={6} borderRadius="lg" boxShadow="md">
          <Heading size="sm" mb={4}>Actions Breakdown</Heading>
          {data.audit_by_action?.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={data.audit_by_action} dataKey="count" nameKey="action" cx="50%" cy="50%" outerRadius={80}
                  label={({ action, count }) => `${action} (${count})`}>
                  {data.audit_by_action.map((item, i) => (
                    <Cell key={i} fill={ACTION_COLORS[item.action] || COLORS[i % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <Text color="gray.400" textAlign="center" py={10}>No data</Text>
          )}
        </Box>

        <Box bg="white" p={6} borderRadius="lg" boxShadow="md">
          <Heading size="sm" mb={4}>Top Active Tenants</Heading>
          <VStack align="stretch" spacing={2}>
            {(data.tenant_activity || []).map((t, i) => (
              <HStack key={i} justify="space-between">
                <Badge colorScheme="purple" fontSize="sm" px={2}>{t.tenant_id}</Badge>
                <Text fontWeight="bold">{t.count} events</Text>
              </HStack>
            ))}
            {(!data.tenant_activity || data.tenant_activity.length === 0) && (
              <Text color="gray.400" textAlign="center" py={4}>No tenant activity</Text>
            )}
          </VStack>
        </Box>
      </SimpleGrid>

      {/* All admin activity over time */}
      <Box bg="white" p={6} borderRadius="lg" boxShadow="md">
        <Heading size="sm" mb={4}>All Admin Activity</Heading>
        {data.audit_by_day?.length > 0 ? (
          <ResponsiveContainer width="100%" height={200}>
            <AreaChart data={data.audit_by_day}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" tickFormatter={formatDate} fontSize={12} />
              <YAxis fontSize={12} />
              <Tooltip labelFormatter={(v) => new Date(v).toLocaleDateString()} />
              <Area type="monotone" dataKey="count" stroke="#6d28d9" fill="#6d28d9" fillOpacity={0.2} name="Events" />
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <Text color="gray.400" textAlign="center" py={10}>No activity data</Text>
        )}
      </Box>

      {/* Action breakdown table */}
      {data.audit_by_action?.length > 0 && (
        <Box bg="white" p={6} borderRadius="lg" boxShadow="md" mt={6}>
          <Heading size="sm" mb={4}>Event Details</Heading>
          <Table size="sm">
            <Thead>
              <Tr>
                <Th>Action</Th>
                <Th isNumeric>Count</Th>
              </Tr>
            </Thead>
            <Tbody>
              {data.audit_by_action.map((item, i) => (
                <Tr key={i}>
                  <Td>
                    <Badge
                      colorScheme={
                        item.action === 'sso_login' ? 'purple' :
                        item.action === 'sso_provision' ? 'green' :
                        item.action === 'create' ? 'blue' :
                        item.action === 'update' ? 'yellow' :
                        item.action === 'delete' ? 'red' : 'gray'
                      }
                    >
                      {item.action}
                    </Badge>
                  </Td>
                  <Td isNumeric fontWeight="bold">{item.count}</Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      )}
    </Box>
  );
}

/** Merge login and provision day series for side-by-side bar chart */
function mergeTimeSeries(logins = [], provisions = []) {
  const map = {};
  for (const item of logins) {
    map[item.date] = { date: item.date, logins: item.count, provisions: 0 };
  }
  for (const item of provisions) {
    if (!map[item.date]) {
      map[item.date] = { date: item.date, logins: 0, provisions: item.count };
    } else {
      map[item.date].provisions = item.count;
    }
  }
  return Object.values(map).sort((a, b) => a.date.localeCompare(b.date));
}

export default UsageAnalytics;
